/**
 * Planetary-strength delivery gate.
 *
 * Classical rule (Vol_05 Aspects/Shadbala/Avasthas; Vol_09 §11.3): a significator only
 * delivers its houses' results to the extent that it is STRONG. A debilitated, combust or
 * otherwise weak graha that "signifies" an event house promises results it cannot give; a
 * strong one delivers in full. Vol_05 states plainly that no prediction is complete without
 * a strength assessment and that effects come through the strongest graha.
 *
 * Design principle — strength MODULATES, it does not ADD. The KP engine ranks birth times by
 * each graha's *signed net significator* strength (lib/kp_scoring + _kp.net_significator_strength),
 * a design built specifically to defeat significator saturation. If we added raw Shadbala as a
 * separate additive vote we would re-introduce the saturation it fights. So strength is applied
 * as a multiplier that scales how far a significator's score may move away from neutral (50):
 * a strong lord delivers its full lean, a weak lord is pulled back toward neutral because it
 * cannot make good on what it signifies.
 *
 * The chart already carries ShadbalaLite (dignity / dig / cheshta / naisargika) for every
 * graha plus the data needed for a combustion check, all computed per candidate inside
 * calculate_chart. This module only reads them, so it adds no ephemeris work.
 */

import {
  DEBILITATIONS,
  EXALTATIONS,
  MOOLATRIKONA,
  OWN_SIGNS,
  compound_relation,
  compute_combustion,
  compute_graha_yuddha,
  is_vargottama,
  norm_deg,
  positional_dignity_strength,
} from "../astro/helpers";
import { SIGN_RULERS } from "../astro/constants";
import { get_house_from_signs } from "../astro/chart";
import { get_divisional_position } from "../astro/transit_data";
import { pymod } from "./pyutil";
import type { PlanetPosition, RawChartData } from "../astro/models";

// ShadbalaLite.total is a 0..1 composite with a neutral midpoint of 0.5. The delivery factor
// swings ±(STRENGTH_SPAN/2) around 1.0 across the full strength range, so an exalted/very strong
// graha delivers up to ~+25% of its net lean and a debilitated/very weak one ~-25%. Kept gentle
// on purpose: strength refines the significator verdict, it must never overpower the event-date
// dasha evidence that actually fixes the time.
export const STRENGTH_SPAN = 0.5;
// A combust graha is swallowed by the Sun's glare and cannot deliver cleanly (Vol_05) — an
// extra multiplicative penalty on top of its Shadbala-derived factor. The penalty is GRADED
// by proximity (02_planetary_strengths.md: "The closer to the Sun, the more complete the
// combustion"; 05_avasthas_baal_adi_nine_dignified.md: "planets very close to Sun (within 1°)
// are most severely combust; planets at outer edge of combustion zone are mildly affected"):
// ×COMBUST_PENALTY_EDGE at the orb edge easing linearly to ×COMBUST_PENALTY_DEEP at ≤1°
// separation. COMBUST_PENALTY (0.85) is the mid value — a planet halfway into its combustion
// zone gets exactly the old flat penalty.
export const COMBUST_PENALTY = 0.85;
export const COMBUST_PENALTY_EDGE = 0.95;
export const COMBUST_PENALTY_DEEP = 0.75;

/** Proximity-graded combustion penalty (see the constants above). */
export function combustion_penalty(orb_deg: number, range_deg: number): number {
  if (range_deg <= 1.0) {
    return COMBUST_PENALTY_DEEP;
  }
  const t = Math.max(0.0, Math.min(1.0, (range_deg - orb_deg) / (range_deg - 1.0)));
  return COMBUST_PENALTY_EDGE - (COMBUST_PENALTY_EDGE - COMBUST_PENALTY_DEEP) * t;
}
// Hard clamp so no single gate can zero-out or balloon a component.
export const STRENGTH_FLOOR = 0.6;
export const STRENGTH_CEIL = 1.3;

// ── Dignity-refinement calibration (02_planetary_dignities.md "Dignity Ratio of Effects") ──
// Doc ratios: Exaltation 100% / Mooltrikona 75% / Own 50% / Friend 25% / Neutral 12.5% /
// Enemy 0%. The gate's dignity scale (positional_dignity_strength — Uccha gradient with
// MT 0.9 / own 0.85 floors) is a compressed proxy of the same ordering. The friendly-sign
// tiers enter two ways:
//   • FLOORS at the doc ratios (friend 0.25 / neutral 0.125) — these only bite near deep
//     debilitation, where the Uccha gradient dips below them;
//   • small multiplicative TIER SCALES on top of the gradient (2026-07-20) — before these, the
//     floors were the ONLY tier mechanism, and since the gradient sits above 0.25 across most
//     of the zodiac, the documented ladder collapsed in practice to "enemy ×0.9, everything
//     else identical". The scales make the ladder real at EVERY degree while staying bounded
//     (all within ±10%): own/MT/exalted ×1.05 (capped at 1.0), friend ×1.0 (reference tier),
//     neutral ×0.97, enemy ×0.9 — never a hard zero, because the gate is a proxy multiplier
//     and must never erase a significator outright.
// At equal gradient value the tier ordering exalt/MT/own > friend > neutral > enemy is strict
// (pinned in strength_scoring.test.ts); across signs the Uccha gradient still dominates by
// design (a friend-sign planet near its exaltation point legitimately outscores an own-sign
// planet far from it — the tiers refine, they do not overrule degree-level strength).
export const FRIEND_SIGN_FLOOR = 0.25;
export const NEUTRAL_SIGN_FLOOR = 0.125;
// Bounded tier scales (see block comment above). Kept conservative: the dignity delta enters
// the final factor at DIGNITY_REFINE_WEIGHT × STRENGTH_SPAN (= ×0.175), so a ±5% tier nudge
// moves the delivery factor by well under 1%.
export const DIGNIFIED_TIER_SCALE = 1.05; // own / moolatrikona / exalted (capped at 1.0)
export const NEUTRAL_SIGN_SCALE = 0.97;
// CALIBRATION: an initial −25% enemy scale (0.75) regressed the accuracy harness (arnold's
// true-sign rank 6→7, clean-chart avg 3.50→3.75), so the reduction is bounded tighter at
// −10%. The doc-required ORDERING is fully preserved; only the magnitude is gentler.
// Re-widen only if a held-out benchmark supports it.
export const ENEMY_SIGN_SCALE = 0.9;

// Vargottama "upgrades effective strength by one tier" (05_strength_assessment_thresholds.md).
// One tier on the doc's ratio ladder is a 12.5–25-point step; on this 0..1 dignity scale we
// take +0.125 (the ladder's lower-half step, conservative), bounded at 1.0. NOT applied to a
// debilitated graha — 02_planetary_dignities.md (Vargottama, Exceptions): "Vargottama in
// debilitation (e.g., Saturn Vargottama in Aries) — some authorities say the debilitation is
// intensified, not cancelled; apply with caution and check Neecha Bhanga separately."
export const VARGOTTAMA_TIER = 0.125;

// Neecha Bhanga "reverses debilitation to strength" (05_strength_assessment_thresholds.md;
// per-planet Exceptions in 02_planetary_dignities.md). Honest bounded read: the cancellation
// lifts the dignity to OWN-SIGN level (doc ratio 0.5) — it restores a working planet, it does
// not manufacture full exaltation.
export const NEECHA_BHANGA_STRENGTH = 0.5;

// Graha Yuddha (02_planetary_strengths.md "Planetary War" / Nipidita avastha): the DEFEATED
// planet's "significations are suppressed and disturbed" — penalised at the same bounded
// magnitude as combustion. The VICTOR "gains Cheshta Bala (motional strength), results
// amplified" per the same section, so it earns a mild lift.
export const WAR_DEFEATED_PENALTY = 0.85;
export const WAR_VICTOR_LIFT = 1.05;

// The dignity-refinement delta re-enters the factor at the SAME weight dignity carries inside
// ShadbalaLite.total (0.35 — see compute_shadbala_lite), times STRENGTH_SPAN: exactly as if
// the shadbala dignity itself had been refined, WITHOUT touching the fixture-pinned chart
// pipeline (chart.shadbala values stay byte-identical to the golden fixtures).
export const DIGNITY_REFINE_WEIGHT = 0.35;

const _GRAHAS = new Set(["sun", "moon", "mars", "mercury", "jupiter", "venus", "saturn", "rahu", "ketu"]);

// Ablation switch for the benchmark harness only. When False, graha_strength_factor returns 1.0
// (no gating), so the harness can measure the strength layer's contribution to accuracy. Always
// True in production; flip it via lib.benchmark's ablation context, never in the live app.
// (Live `let` binding + setter seam: TS modules cannot be monkeypatched like Python module attrs.)
export let GATING_ENABLED = true;

export function set_gating_enabled(value: boolean): void {
  GATING_ENABLED = value;
}

const _KENDRA_HOUSES = new Set([1, 4, 7, 10]);
const _SEVEN_GRAHAS = ["sun", "moon", "mars", "mercury", "jupiter", "venus", "saturn"];

/** Whole-sign house (1..12) of `sign` counted from `from_sign`, tested against kendras. */
function _in_kendra(sign: number, from_sign: number): boolean {
  return _KENDRA_HOUSES.has(get_house_from_signs(sign, from_sign));
}

/**
 * Neecha Bhanga — cancellation of debilitation (02_planetary_dignities.md, the per-planet
 * "Exceptions" lines, e.g. Sun Debilitation). True when the graha sits in its debilitation
 * sign AND any of the classical conditions holds:
 *   (a) the LORD of the debilitation sign (dispositor) is in a kendra from Lagna or Moon;
 *   (b) the planet EXALTED in that debilitation sign is in a kendra from Lagna or Moon;
 *   (c) the debilitated planet itself is in a kendra from the LAGNA while its dispositor
 *       stands in its own or exaltation sign.
 * Kendras are whole-sign (1/4/7/10). The Moon is trivially in the 1st from itself, so the
 * Moon reference is skipped when the body being LOCATED is the Moon — otherwise Mars-in-Cancer
 * would get automatic cancellation through its own dispositor.
 */
export function neecha_bhanga_applies(chart: RawChartData, graha: string): boolean {
  const g = (graha || "").trim().toLowerCase();
  const pos = chart.planets[g];
  const debil_sign = DEBILITATIONS[g];
  if (pos === undefined || debil_sign === undefined || pos.sign !== debil_sign) {
    return false;
  }
  const asc_sign = Math.trunc(Math.floor(norm_deg(chart.ascendant_longitude) / 30));
  const moon = chart.planets["moon"];
  const in_kendra_from_ref = (name: string, p: PlanetPosition): boolean =>
    _in_kendra(p.sign, asc_sign) ||
    (moon !== undefined && name !== "moon" && _in_kendra(p.sign, moon.sign));

  const dispositor = SIGN_RULERS[debil_sign];
  const dpos = dispositor !== undefined ? chart.planets[dispositor] : undefined;
  // (a) dispositor in kendra from Lagna or Moon
  if (dpos !== undefined && in_kendra_from_ref(dispositor, dpos)) {
    return true;
  }
  // (b) the planet exalted in the debilitation sign in kendra from Lagna or Moon
  // (classical 7-graha exaltation scheme — the doc's per-planet lines all name classical grahas)
  const exalt_lord = _SEVEN_GRAHAS.find((q) => EXALTATIONS[q] === debil_sign);
  const epos = exalt_lord !== undefined ? chart.planets[exalt_lord] : undefined;
  if (exalt_lord !== undefined && epos !== undefined && in_kendra_from_ref(exalt_lord, epos)) {
    return true;
  }
  // (c) debilitated planet in kendra from Lagna while its dispositor is in own/exaltation sign
  if (dpos !== undefined && _in_kendra(pos.sign, asc_sign)) {
    if ((OWN_SIGNS[dispositor] ?? []).includes(dpos.sign) || dpos.sign === EXALTATIONS[dispositor]) {
      return true;
    }
  }
  return false;
}

// Graha yuddha detection is pairwise over the chart's planets; memoised per chart object so
// the many per-lord gate calls inside a candidate share one computation.
const _war_cache = new WeakMap<RawChartData, Array<Record<string, any>>>();

// Final delivery factor per (chart, graha) — see graha_strength_factor.
const _strength_cache = new WeakMap<RawChartData, Map<string, number>>();

/**
 * War verdict for a graha: "defeated" | "victor" | null (not at war / exempt).
 * 02_planetary_strengths.md "Planetary War": within 1° in the same sign, the planet with the
 * LOWER degree is the victor and the HIGHER degree is defeated (Sun/Moon/nodes exempt — the
 * detector already only pairs Mercury/Venus/Mars/Jupiter/Saturn). A graha defeated in ANY war
 * counts as defeated (Nipidita — suppressed).
 */
export function graha_war_status(chart: RawChartData, graha: string): "victor" | "defeated" | null {
  const g = (graha || "").trim().toLowerCase();
  let wars = _war_cache.get(chart);
  if (wars === undefined) {
    wars = compute_graha_yuddha(chart.planets);
    _war_cache.set(chart, wars);
  }
  let status: "victor" | null = null;
  for (const war of wars) {
    if (war["p1"] !== g && war["p2"] !== g) {
      continue;
    }
    // The detector resolves the victor (the planet BEHIND along the short arc — the docs'
    // "lower degree" rule, cusp-safe); defeat in ANY war dominates.
    if (war["defeated"] === g) {
      return "defeated";
    }
    status = "victor";
  }
  return status;
}

/**
 * Doc-refined positional dignity for the delivery gate. Returns the raw gate dignity
 * (positional_dignity_strength — the value already baked into ShadbalaLite) alongside the
 * REFINED value that additionally carries:
 *   - COMPOUND friend/neutral/enemy sign tiers (02_planetary_relationships.md: compound =
 *     natural + temporary, "the definitive relationship used for interpretation"),
 *   - Neecha Bhanga cancellation of debilitation,
 *   - the Vargottama one-tier upgrade (withheld in debilitation — contested — and withheld
 *     for combust/war-defeated grahas: 03_varga_system_overview.md "Its Varga strength is to
 *     be disregarded as auspicious").
 * The gate applies only the DELTA between them, so nothing is double-counted against the
 * fixture-pinned shadbala composite.
 */
export function refined_dignity(chart: RawChartData, graha: string): { base: number; refined: number } {
  const g = (graha || "").trim().toLowerCase();
  const pos = chart.planets[g];
  if (pos === undefined) {
    return { base: 0.5, refined: 0.5 };
  }
  const base = positional_dignity_strength(g, pos.longitude, pos.sign);
  let refined = base;
  const sign = pos.sign;
  const is_debilitated = DEBILITATIONS[g] === sign;
  const in_dignified_sign =
    EXALTATIONS[g] === sign ||
    (OWN_SIGNS[g] ?? []).includes(sign) ||
    (g in MOOLATRIKONA && MOOLATRIKONA[g][0] === sign);

  if (!is_debilitated && !in_dignified_sign) {
    // Friend/neutral/enemy tiers apply only in "other" signs — exalt/MT/own/debil signs carry
    // their own dignities. The tier comes from the COMPOUND relation (natural + temporary —
    // 02_planetary_relationships.md: temporary friend = dispositor placed 2/3/4/10/11/12 from
    // the graha). Great-friend/friend share the friend tier and enemy/great-enemy the enemy
    // tier — the doc's dignity ratios name only friend/neutral/enemy tiers, so no new numbers
    // are invented. When the dispositor is absent from the chart the compound relation
    // degrades to the natural one (nodes then come back NEUTRAL — dispositor-dominant per
    // 02_rahu_ketu_rules.md; the classical friendship table has no node rows).
    // Each tier = bounded scale + doc-ratio floor (see the calibration block above): friend is
    // the ×1.0 reference, neutral eases 3% below it, enemy 10% — so the documented ladder is
    // strict at every degree instead of only within ~45° of deep debilitation.
    const rel = compound_relation(chart, g, SIGN_RULERS[sign] ?? "");
    if (rel === "great_friend" || rel === "friend") {
      refined = Math.max(refined, FRIEND_SIGN_FLOOR);
    } else if (rel === "enemy" || rel === "great_enemy") {
      refined = refined * ENEMY_SIGN_SCALE;
    } else {
      refined = Math.max(refined * NEUTRAL_SIGN_SCALE, NEUTRAL_SIGN_FLOOR);
    }
  } else if (!is_debilitated && in_dignified_sign) {
    // Own/MT/exalted signs sit one bounded tier ABOVE the friend reference (doc ladder:
    // exalt > MT > own > friend). Capped at 1.0 — deep exaltation stays the ceiling.
    refined = Math.min(1.0, refined * DIGNIFIED_TIER_SCALE);
  }
  if (is_debilitated && neecha_bhanga_applies(chart, g)) {
    refined = Math.max(refined, NEECHA_BHANGA_STRENGTH);
  }
  if (!is_debilitated && is_vargottama(pos.longitude)) {
    // Vargottama is a VARGA credit — 03_varga_system_overview.md: "a combust planet's Varga
    // strength is to be disregarded as auspicious" (same for war-defeated / bad-Avastha), so
    // the one-tier lift is withheld under those afflictions.
    const sun = chart.planets["sun"];
    const combust =
      sun !== undefined &&
      Boolean(
        compute_combustion(sun.longitude, pos.longitude, g, Boolean(pos.is_retrograde ?? false))[
          "is_combust"
        ]
      );
    if (!combust && graha_war_status(chart, g) !== "defeated") {
      refined = Math.min(1.0, refined + VARGOTTAMA_TIER);
    }
  }
  return { base, refined };
}

// ── Vimshopaka Bala (03_vimshopaka_bala.md) ───────────────────────────────────────────────
// Shad Varga (6-chart) scheme — "Rasi: 6 points, Hora: 2, Drekkana: 4, Navamsa: 5,
// Dvadasamsa: 2, Trimshamsa: 1 — Total: 20 points maximum." (The doc's other schemes need
// D-7/D-16/D-60 etc.; the Shad Varga is fully covered by the canonical varga engine and the
// doc presents it first.)
const VIMSHOPAKA_SHAD_VARGA: Array<[division: number, points: number]> = [
  [1, 6.0],
  [2, 2.0],
  [3, 4.0],
  [9, 5.0],
  [12, 2.0],
  [30, 1.0],
];

// Varga Vishwa (per-varga score out of 20, doc tiers):
//   own sign / exaltation in the varga → 20 ("the FULL Varga Vishwa score (20/20)")
//   friendly sign (Pramudit)           → 18
//   neutral sign (Shanta)              → 15
//   enemy sign (Duhkhita)              →  7
//   great-enemy sign (Khala)           →  5 ("minimum is 5, NOT zero")
//   debilitation                       →  0 ("absolute debilitation … mapped to the
//                                            'sleeping'/nil-result state")
// Friendly/enemy tiers use the COMPOUND relation to the varga sign's lord (temporary part
// from the D-1 chart, the classical practice). The doc's 10-point Svastha tier is, in its own
// words, "an unnamed intermediate dignity step" it declines to attribute — the compound
// ladder has no state between friend and neutral, so that tier is unreachable here.
const _VARGA_VISHWA_BY_RELATION: Record<string, number> = {
  great_friend: 18.0,
  friend: 18.0,
  neutral: 15.0,
  enemy: 7.0,
  great_enemy: 5.0,
};

/**
 * Vimshopaka Bala 0..20 for a graha over the Shad Varga scheme (03_vimshopaka_bala.md).
 * Each varga contributes its point allocation × (Varga Vishwa / 20).
 */
export function vimshopaka_bala(chart: RawChartData, graha: string): number {
  const g = (graha || "").trim().toLowerCase();
  const pos = chart.planets[g];
  if (pos === undefined) {
    return 10.0; // unknown body: mid-scale (neutral)
  }
  let total = 0.0;
  for (const [division, points] of VIMSHOPAKA_SHAD_VARGA) {
    const [varga_sign] = get_divisional_position(pos.longitude, division);
    let vishwa: number;
    if (DEBILITATIONS[g] === varga_sign) {
      vishwa = 0.0;
    } else if (EXALTATIONS[g] === varga_sign || (OWN_SIGNS[g] ?? []).includes(varga_sign)) {
      vishwa = 20.0;
    } else {
      const rel = compound_relation(chart, g, SIGN_RULERS[varga_sign] ?? "");
      vishwa = _VARGA_VISHWA_BY_RELATION[rel] ?? 15.0;
    }
    total += points * (vishwa / 20.0);
  }
  return total;
}

// Bounded confirmation-style multiplier from the 0..20 Vimshopaka scale, consistent with the
// gate's other multiplicative checks (combustion/war — never a hard zero). Mapping
// (documented): ×(MIN + (MAX−MIN)·score/20), i.e. the doc's "below 5 → CANNOT give auspicious
// results" band lands at the strongest reduction (×0.90..×0.9375), "above 15 → wholly
// favorable" gives a mild lift (×1.0125..×1.05), and the neutral point (×1.0) sits at
// score = 13⅓, inside the doc's "10–15 mediocre, mixed results" band.
export const VIMSHOPAKA_MULT_MIN = 0.9;
export const VIMSHOPAKA_MULT_MAX = 1.05;

/** Map a 0..20 Vimshopaka Bala onto the bounded gate multiplier (see constants above). */
export function vimshopaka_multiplier(score: number): number {
  const s = Math.max(0.0, Math.min(20.0, Number(score))) / 20.0;
  return VIMSHOPAKA_MULT_MIN + (VIMSHOPAKA_MULT_MAX - VIMSHOPAKA_MULT_MIN) * s;
}

/**
 * Delivery multiplier (~0.6..1.3) for a graha, from its Shadbala-lite total refined by the
 * doc dignity tiers (friend/neutral/enemy, Vargottama, Neecha Bhanga), graha-yuddha verdict,
 * combustion (proximity-graded) and Vimshopaka Bala.
 *
 * 1.0 means an average-strength graha that delivers its significator lean as-is. Above 1.0
 * the graha is strong (exalted / own / well-placed / fast / war victor) and over-delivers;
 * below 1.0 it is weak (debilitated / enemy sign / poorly placed), war-defeated or combust
 * and under-delivers. Unknown grahas (none, angles) return 1.0 so the gate is a no-op.
 */
export function graha_strength_factor(chart: RawChartData, graha: string | null | undefined): number {
  if (!GATING_ENABLED) {
    return 1.0;
  }
  const g = (graha || "").trim().toLowerCase();
  if (!_GRAHAS.has(g)) {
    return 1.0;
  }
  // Pure per (chart, graha) — but called for every level lord / sub-lord / RP lord of every
  // event of a candidate. Memoise per chart object (same pattern as _war_cache above); the
  // GATING_ENABLED ablation seam returns before the cache, so cached values stay pure.
  let per_chart = _strength_cache.get(chart);
  if (per_chart === undefined) {
    per_chart = new Map<string, number>();
    _strength_cache.set(chart, per_chart);
  }
  const cached = per_chart.get(g);
  if (cached !== undefined) {
    return cached;
  }
  const shadbala = chart.shadbala ?? {};
  const sb = shadbala[g];
  if (sb === undefined || sb === null) {
    return 1.0;
  }
  const total = Math.max(0.0, Math.min(1.0, Number(sb.total ?? 0.5)));
  let factor = 1.0 + (total - 0.5) * STRENGTH_SPAN;
  // Dignity refinement delta, weighted exactly as dignity is weighted inside sb.total.
  const { base, refined } = refined_dignity(chart, g);
  factor += (refined - base) * DIGNITY_REFINE_WEIGHT * STRENGTH_SPAN;
  // Graha yuddha: defeated is suppressed (combustion-magnitude), victor mildly amplified.
  const war = graha_war_status(chart, g);
  if (war === "defeated") {
    factor *= WAR_DEFEATED_PENALTY;
  } else if (war === "victor") {
    factor *= WAR_VICTOR_LIFT;
  }
  const sun = chart.planets["sun"];
  const pos = chart.planets[g];
  let is_combust = false;
  if (sun !== undefined && pos !== undefined) {
    const is_retro = Boolean(pos.is_retrograde ?? false);
    const comb = compute_combustion(sun.longitude, pos.longitude, g, is_retro);
    if (comb["is_combust"]) {
      is_combust = true;
      // Graded by proximity — deepest (×0.75) within 1° "Moudyami", mildest (×0.95) at the
      // orb edge (02_planetary_strengths.md; 05_avasthas_baal_adi_nine_dignified.md).
      factor *= combustion_penalty(Number(comb["orb_deg"]), Number(comb["range_deg"]));
    }
  }
  // Vimshopaka Bala confirmation gate (03_vimshopaka_bala.md). Per the doc's Exceptions —
  // "If the planet is combust or defeated in planetary war, the Vimshopaka Bala score is to
  // be disregarded" — the auspicious (>×1.0) half is withheld for such grahas; the reduction
  // half still applies (a weak Vimshopaka planet is weak regardless).
  if (pos !== undefined) {
    let vmult = vimshopaka_multiplier(vimshopaka_bala(chart, g));
    if ((is_combust || war === "defeated") && vmult > 1.0) {
      vmult = 1.0;
    }
    factor *= vmult;
  }
  const clamped = Math.max(STRENGTH_FLOOR, Math.min(STRENGTH_CEIL, factor));
  per_chart.set(g, clamped);
  return clamped;
}

/**
 * Scale a 0..100 component score's distance from neutral by a strength factor.
 *
 * A weak significator (factor < 1) is pulled back toward 50 because it cannot deliver what
 * it signifies; a strong one (factor > 1) is pushed further from 50. Result stays in 0..100.
 */
export function gate_toward_neutral(raw_score: number, factor: number, neutral = 50.0): number {
  return Math.max(0.0, Math.min(100.0, neutral + (Number(raw_score) - neutral) * Number(factor)));
}

/** Convenience: gate a lord's component score by that lord's own strength factor. */
export function gate_lord_score(
  chart: RawChartData,
  lord: string | null | undefined,
  raw_score: number,
  neutral = 50.0,
): number {
  return gate_toward_neutral(raw_score, graha_strength_factor(chart, lord), neutral);
}
